// Порт «онлайн/офлайн» поверх navigator.onLine + событий online/offline. SPEC client-transport §4.4.
//
// Reconnect слушает возврат сети (немедленная попытка вместо ожидания таймера) и пропажу (не крутит
// таймер впустую). В браузере — реальный navigator/window; в тестах — управляемый фейк (create
// ManualNetworkMonitor) либо «всегда онлайн» для сред без navigator.
/**
 * Дефолтный монитор: читает `navigator.onLine`, подписывается на `window` events `online`/`offline`.
 * Если окружение без navigator/window (SSR/тест без фейка) — считает «всегда онлайн» и no-op подписки.
 */
export function createBrowserNetworkMonitor() {
    const hasNavigator = typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean';
    const hasWindow = typeof window !== 'undefined' && typeof window.addEventListener === 'function';
    return {
        isOnline() {
            return hasNavigator ? navigator.onLine : true;
        },
        onOnline(handler) {
            if (!hasWindow)
                return () => { };
            window.addEventListener('online', handler);
            return () => window.removeEventListener('online', handler);
        },
        onOffline(handler) {
            if (!hasWindow)
                return () => { };
            window.addEventListener('offline', handler);
            return () => window.removeEventListener('offline', handler);
        },
    };
}
/**
 * Управляемый монитор для тестов: `setOnline(v)` дёргает соответствующих подписчиков. Имитирует
 * navigator.onLine + online/offline события без браузера.
 */
export class ManualNetworkMonitor {
    online;
    onlineHandlers = new Set();
    offlineHandlers = new Set();
    constructor(initialOnline = true) {
        this.online = initialOnline;
    }
    isOnline() {
        return this.online;
    }
    onOnline(handler) {
        this.onlineHandlers.add(handler);
        return () => this.onlineHandlers.delete(handler);
    }
    onOffline(handler) {
        this.offlineHandlers.add(handler);
        return () => this.offlineHandlers.delete(handler);
    }
    /** Переключить состояние сети и оповестить подписчиков (если состояние изменилось). */
    setOnline(value) {
        if (this.online === value)
            return;
        this.online = value;
        const handlers = value ? this.onlineHandlers : this.offlineHandlers;
        for (const h of [...handlers])
            h();
    }
}
/**
 * Дефолтный монитор видимости (VIS-1): `document.visibilityState !== 'hidden'` — «видна» (так и
 * `prerender`/`unloaded` старых движков не блокируют соединение). Возврат на экран — событие
 * `visibilitychange` в visible ИЛИ `pageshow` (восстановление из bfcache, iOS после долгого фона).
 * Окружение без document (SSR/node-тесты) — «всегда видна», подписки no-op.
 */
export function createBrowserVisibilityMonitor() {
    const hasDocument = typeof document !== 'undefined' && typeof document.addEventListener === 'function';
    const hasWindow = typeof window !== 'undefined' && typeof window.addEventListener === 'function';
    const isVisible = () => !hasDocument || document.visibilityState !== 'hidden';
    return {
        isVisible,
        onVisible(handler) {
            if (!hasDocument)
                return () => { };
            const onChange = () => {
                if (isVisible())
                    handler();
            };
            document.addEventListener('visibilitychange', onChange);
            if (hasWindow)
                window.addEventListener('pageshow', onChange);
            return () => {
                document.removeEventListener('visibilitychange', onChange);
                if (hasWindow)
                    window.removeEventListener('pageshow', onChange);
            };
        },
    };
}
/** Управляемый монитор видимости для тестов: `setVisible(true)` зовёт подписчиков onVisible. */
export class ManualVisibilityMonitor {
    visible;
    handlers = new Set();
    constructor(initialVisible = true) {
        this.visible = initialVisible;
    }
    isVisible() {
        return this.visible;
    }
    onVisible(handler) {
        this.handlers.add(handler);
        return () => this.handlers.delete(handler);
    }
    setVisible(value) {
        if (this.visible === value)
            return;
        this.visible = value;
        if (value)
            for (const h of [...this.handlers])
                h();
    }
}
//# sourceMappingURL=network.js.map